import { create } from 'zustand';

// Phase 1 screen share — visitor's MediaRecorder ships ~200ms webm
// chunks over the WS and CRM relays them to this rep's personal topic.
// The store keys live state per conversation so multiple ongoing
// shares stay isolated (we don't want chunks from another tab's chat
// landing in the viewer for this conversation).
//
// We deliberately keep this store ephemeral — no persistence, no
// rehydration. If the rep refreshes mid-share, they reconnect to a
// fresh stream once the visitor's next chunk arrives.

export interface ScreenShareEntry {
  conversationId: number;
  mime: string;
  // Chunks waiting to be appended to the viewer's SourceBuffer. The
  // viewer drains this on each store update — when the component is
  // unmounted (e.g. rep navigated away mid-share) chunks accumulate
  // until either it remounts or the share ends.
  pending: Uint8Array[];
  // Latched once `screen_ended` arrives so the viewer can call
  // MediaSource.endOfStream() and present a final paused frame.
  ended: boolean;
}

interface ScreenShareState {
  shares: Record<number, ScreenShareEntry>;
  start: (conversationId: number, mime: string) => void;
  pushChunk: (conversationId: number, bytes: Uint8Array) => void;
  // Viewers call this after appending to their SourceBuffer to remove
  // already-consumed chunks. Otherwise the buffer grows unbounded.
  drain: (conversationId: number, count: number) => void;
  end: (conversationId: number) => void;
  // Tear-down: viewer unmounting or conversation closing. Drops all
  // buffered chunks for that conversation; the next `start` event
  // sets up a fresh entry.
  clear: (conversationId: number) => void;
}

export const useScreenShareStore = create<ScreenShareState>((set) => ({
  shares: {},
  start: (conversationId, mime) =>
    set((state) => ({
      shares: {
        ...state.shares,
        [conversationId]: { conversationId, mime, pending: [], ended: false },
      },
    })),
  pushChunk: (conversationId, bytes) =>
    set((state) => {
      const existing = state.shares[conversationId];
      if (!existing) return state;
      return {
        shares: {
          ...state.shares,
          [conversationId]: { ...existing, pending: [...existing.pending, bytes] },
        },
      };
    }),
  drain: (conversationId, count) =>
    set((state) => {
      const existing = state.shares[conversationId];
      if (!existing) return state;
      return {
        shares: {
          ...state.shares,
          [conversationId]: { ...existing, pending: existing.pending.slice(count) },
        },
      };
    }),
  end: (conversationId) =>
    set((state) => {
      const existing = state.shares[conversationId];
      if (!existing) return state;
      return {
        shares: {
          ...state.shares,
          [conversationId]: { ...existing, ended: true },
        },
      };
    }),
  clear: (conversationId) =>
    set((state) => {
      if (!state.shares[conversationId]) return state;
      const next = { ...state.shares };
      delete next[conversationId];
      return { shares: next };
    }),
}));

// Base64 helper used by the WebchatAgent receiver to decode the
// visitor's chunk before pushing into the store. Kept colocated so
// the agent hook doesn't grow a tiny util file just for this.
export function decodeBase64Chunk(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
